import React, { useState } from 'react';
import type { Cliente } from '../api/clientesApiSlice';
import { useUpdateClienteMutation, useDeleteClienteMutation } from '../api/clientesApiSlice';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../auth/authSlice';
import { useGetEmpresaByIdQuery } from '../../empresas/api/empresasApiSlice';

interface ClienteDetalleModalProps {
  cliente: Cliente;
  onClose: () => void;
  onSave?: () => void;
  onDelete?: () => void;
}

const camposMostrar = [
  { label: 'Documento', key: 'documento' },
  { label: 'Teléfono', key: 'telefono' },
  { label: 'Apellido', key: 'apellido' },
  { label: 'Nombre', key: 'nombre' },
  { label: 'Dirección', key: 'direccion' },
  { label: 'Localidad', key: 'localidad' },
  { label: 'Provincia', key: 'provincia' },
  { label: 'País', key: 'pais' },
  { label: 'Email', key: 'email' },
  { label: 'Facebook', key: 'facebook' },
  { label: 'Twitter', key: 'twitter' },
  { label: 'Instagram', key: 'instagram' },
  { label: 'Linkedin', key: 'linkedin' },
  { label: 'Github', key: 'github' },
  { label: 'Condición', key: 'condicion' },
  // { label: 'Empresa ID', key: 'empresa_id' },
  // { label: 'ID1', key: 'id1' },
  // { label: 'ID2', key: 'id2' },
  // { label: 'ID3', key: 'id3' },
  // { label: 'ID4', key: 'id4' },
  // { label: 'ID5', key: 'id5' },
];

const ClienteDetalleModal: React.FC<ClienteDetalleModalProps> = ({ cliente, onClose, onSave, onDelete }) => {
  const currentUser = useSelector(selectCurrentUser);
  const empresaId = currentUser?.empresa_id;
  const { data: empresaApi } = useGetEmpresaByIdQuery(
    empresaId ? empresaId.toString() : '', 
    { skip: !empresaId }
  );
  const empresa = empresaApi;
  
  // Obtener etiquetas personalizadas para los campos de texto desde la empresa del usuario
  const camposTextoLabels = [
    { campo: 'texto1', label: empresa?.ntcliente1 || 'Texto 1' },
    { campo: 'texto2', label: empresa?.ntcliente2 || 'Texto 2' },
    { campo: 'texto3', label: empresa?.ntcliente3 || 'Texto 3' },
    { campo: 'texto4', label: empresa?.ntcliente4 || 'Texto 4' },
    { campo: 'texto5', label: empresa?.ntcliente5 || 'Texto 5' },
  ];

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<Partial<Cliente>>(cliente);
  const [clienteActual, setClienteActual] = useState<Cliente>(cliente);
  const [updateCliente] = useUpdateClienteMutation();
  const [deleteCliente] = useDeleteClienteMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => setEditMode(true);
  const handleCancel = () => {
    setEditMode(false);
    setForm(clienteActual);
    setIsError(false);
    setIsSuccess(false);
    setErrorMsg('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setIsError(false);
    setIsSuccess(false);
    setErrorMsg('');
    try {
      const updated = await updateCliente({
        id: cliente.id,
        ...form
      }).unwrap();
      setIsSuccess(true);
      setEditMode(false);
      setForm(updated);
      setClienteActual(updated);
      if (onSave) onSave();
    } catch (err) {
      setIsError(true);
      setErrorMsg((err as {data?: {message?: string}})?.data?.message || 'Error al guardar los cambios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setIsError(false);
    setErrorMsg('');
    try {
      await deleteCliente(cliente.id).unwrap();
      if (onDelete) onDelete();
    } catch (err) {
      setIsError(true);
      setErrorMsg((err as {data?: {message?: string}})?.data?.message || 'Error al eliminar el cliente');
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-card dark:bg-[#181A20] text-card-foreground dark:text-white rounded-lg shadow-lg max-w-2xl w-full p-8 relative border border-border max-h-[90vh] overflow-y-auto">
        <button
          className="absolute top-4 right-4 text-xl text-muted-foreground hover:text-destructive"
          onClick={onClose}
          aria-label="Cerrar"
        >
          ×
        </button>
        <h2 className="text-2xl font-bold mb-6 text-foreground dark:text-white">Detalle del Cliente</h2>
        {!editMode ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {camposMostrar.map(({ label, key }) => (
                <div key={key}>
                  <span className="block text-xs text-muted-foreground dark:text-gray-400 font-semibold mb-1">{label}</span>
                  <span className="block text-base break-all">{clienteActual[key as keyof Cliente] ?? '-'}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4 mt-4">
              <h3 className="text-lg font-semibold mb-2 text-foreground dark:text-white">Campos Personalizados</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {camposTextoLabels.map(({campo, label}) => (
                  <div key={campo}> 
                    <span className="block text-xs text-muted-foreground dark:text-gray-400 font-semibold mb-1">{label}</span>
                    <span className="block text-base break-all">{clienteActual[campo as keyof Cliente] ?? '-'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end mt-6 gap-2">
              <button
                className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-semibold hover:bg-primary/90 transition dark:bg-blue-700 dark:text-white dark:hover:bg-blue-800"
                onClick={handleEdit}
              >
                Editar
              </button>
              <button
                className="bg-destructive text-white px-4 py-2 rounded-md font-semibold hover:bg-destructive/80 transition"
                onClick={() => setConfirmDelete(true)}
                disabled={isLoading}
              >
                Eliminar
              </button>
            </div>
            {confirmDelete && (
              <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/60">
                <div className="bg-card dark:bg-[#181A20] p-6 rounded-lg shadow-lg border border-border max-w-sm w-full">
                  <h4 className="text-lg font-semibold mb-4 text-foreground dark:text-white">¿Confirmar eliminación?</h4>
                  <div className="flex justify-end gap-2">
                    <button
                      className="bg-muted text-foreground px-4 py-2 rounded-md font-semibold hover:bg-accent transition"
                      onClick={() => setConfirmDelete(false)}
                      disabled={isLoading}
                    >
                      Cancelar
                    </button>
                    <button
                      className="bg-destructive text-white px-4 py-2 rounded-md font-semibold hover:bg-destructive/80 transition disabled:opacity-50"
                      onClick={handleDelete}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  </div>
                  {isError && <div className="mt-4 text-destructive dark:text-red-400">{errorMsg}</div>}
                </div>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {camposMostrar.map(({ label, key }) => (
                <div key={key}>
                  <label className="block text-xs text-muted-foreground dark:text-gray-400 font-semibold uppercase mb-1" htmlFor={key}>{label}</label>
                  <input
                    type="text"
                    id={key}
                    name={key}
                    value={form[key as keyof Cliente] ?? ''}
                    onChange={handleChange}
                    className="w-full rounded-md border px-3 py-2 bg-background dark:bg-[#23272F] border-border text-foreground dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4 mt-4">
              <h3 className="text-lg font-semibold mb-2 text-foreground dark:text-white">Campos de Texto Personalizados</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {camposTextoLabels.map(({campo, label}) => (
                  <div key={campo}> 
                    <label className="block text-xs text-muted-foreground dark:text-gray-400 font-semibold mb-1" htmlFor={campo}>{label}</label>
                    <textarea
                      id={campo}
                      name={campo}
                      value={form[campo as keyof Cliente] ?? ''}
                      onChange={handleChange}
                      className="w-full rounded-md border px-3 py-2 bg-background dark:bg-[#23272F] border-border text-foreground dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={2}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end mt-6 gap-2">
              <button
                type="button"
                className="bg-muted text-foreground px-4 py-2 rounded-md font-semibold hover:bg-accent transition"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-semibold hover:bg-primary/90 transition dark:bg-blue-700 dark:text-white dark:hover:bg-blue-800 disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
            {isSuccess && <div className="mt-4 text-success dark:text-green-400">Cambios guardados exitosamente.</div>}
            {isError && <div className="mt-4 text-destructive dark:text-red-400">{errorMsg}</div>}
          </form>
        )}
      </div>
    </div>
  );
};

export default ClienteDetalleModal; 
